// GPX 1.1 export — pure string building, shared by both apps (only the download/share
// step is platform-specific). Output round-trips through each app's parseGPX: one <trk>
// with one <trkseg>, optional <wpt> entries for plan waypoints.

import type { PlanWaypoint } from './savedRoutes';

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** 6 decimals ≈ 0.1 m — matches the precision GPS fixes carry. */
function coord(value: number): string {
  return value.toFixed(6);
}

/**
 * Builds a GPX 1.1 document for a route. `coordinates` are `[lon, lat]` pairs (GeoJSON
 * order, as decoded route geometry flows through both apps).
 */
export function buildRouteGpx(
  name: string,
  coordinates: [number, number][],
  waypoints?: PlanWaypoint[],
): string {
  const safeName = escapeXml(name);

  const wpts = (waypoints ?? [])
    .map((w) => {
      const label = w.name ? `<name>${escapeXml(w.name)}</name>` : '';
      return `  <wpt lat="${coord(w.lat)}" lon="${coord(w.lon)}">${label}</wpt>\n`;
    })
    .join('');

  const trkpts = coordinates
    .map(([lon, lat]) => `      <trkpt lat="${coord(lat)}" lon="${coord(lon)}"/>\n`)
    .join('');

  return (
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    '<gpx version="1.1" creator="MotoGo" xmlns="http://www.topografix.com/GPX/1/1">\n' +
    `  <metadata><name>${safeName}</name></metadata>\n` +
    wpts +
    '  <trk>\n' +
    `    <name>${safeName}</name>\n` +
    '    <trkseg>\n' +
    trkpts +
    '    </trkseg>\n' +
    '  </trk>\n' +
    '</gpx>\n'
  );
}

/** Filesystem-safe filename for a route export, e.g. "Eifel loop" → "eifel-loop.gpx". */
export function gpxFileName(routeName: string): string {
  const slug = routeName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${slug || 'route'}.gpx`;
}
